<template>
  <slide-out
    dock="right"
    :size="sideOutCurrentSize"
    ref="userSlideout"
    @resize="OnResize"
    :min-size="sideOutMinSize"
    :allow-resize="nested ? false : true"
    :append-to="nested ? '' : '#app'"
    :visible.sync="slideouts.active"
    v-on:close="onSlideoutClosing">
    <template v-slot:header>
      <h3 v-if="isFetching" class="font-weight-bold pa-1" small>
        <v-progress-circular :size="16" width="2" indeterminate class="mr-1"></v-progress-circular>
        Fetching User ...
      </h3>
      <h3 v-else-if="selected.id" class="font-weight-bold pa-1" small>
        <v-icon left>mdi-pencil-box-outline</v-icon> Update: '{{
          selected.firstName + ' ' + selected.lastName
        }}'
      </h3>
      <h3 v-else class="font-weight-bold pa-1" small>
        <v-icon left>mdi-plus-box-outline</v-icon> Invite new user to this company!
      </h3>
      <v-btn @click="closeUpdateSlideout()" icon>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </template>

    <div>
      <div class="text-center d-flex flex-column" v-if="isFetching">
        <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">Loading User...</p>
      </div>
      <v-form v-else class="pa-4" ref="updateForm" v-model="valid">
        <v-text-field
          v-model="selected.firstName"
          dense
          :rules="[formRules.required]"
          outlined
          label="First Name"
          placeholder="First Name"></v-text-field>
        <v-text-field
          v-model="selected.lastName"
          :rules="[formRules.required]"
          dense 
          outlined
          label="Last Name"
          placeholder="Last Name"></v-text-field>
        <v-text-field
          v-model="selected.email"
          dense
          outlined
          :rules="selected.id ? [] : [formRules.required]"
          :disabled="!!selected.id"
          type="email"
          label="Email"
          placeholder="Email"></v-text-field>
        <roles-selector
          v-model="selected.roleId"
          :rules="[formRules.required]"
          attach></roles-selector>
        <v-select
          :items="visibleFacilities"
          dense
          outlined
          class="facilities-select"
          item-text="name"
          item-value="id"
          multiple
          small-chips
          attach
          v-model="selected.facilityIds"
          :label="`Facilities (${selectedFacilitiesCount}/${facilitiesList.length})`"
          placeholder="Select Facilities">
          <template v-slot:prepend-item>
            <div class="facilities-select-header">
              <v-text-field
                v-model="facilitySearch"
                dense
                outlined
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                placeholder="Search facilities..."
                class="px-2 pt-2 pb-1"
                @click.stop></v-text-field>
              <div class="d-flex align-center">
                <v-list-item
                  ripple
                  class="flex-grow-1"
                  @mousedown.prevent
                  @click="toggleAllFacilities">
                  <v-list-item-action class="mr-3">
                    <v-icon :color="someVisibleFacilitiesSelected ? 'primary' : ''">
                      {{ selectAllFacilitiesIcon }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ allVisibleFacilitiesSelected ? 'Deselect All' : 'Select All' }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-btn
                  small
                  text
                  class="mr-2 text-none"
                  :color="showSelectedOnly ? 'primary' : 'grey darken-1'"
                  @click="showSelectedOnly = !showSelectedOnly">
                  <v-icon small left>
                    {{ showSelectedOnly ? 'mdi-check-circle' : 'mdi-check-circle-outline' }}
                  </v-icon>
                  Show selected ({{ selectedFacilitiesCount }})
                </v-btn>
              </div>
              <v-divider></v-divider>
            </div>
          </template>
        </v-select>
      </v-form>
    </div>
    <template v-slot:footer>
      <v-card-actions>
        <v-btn text color="error" @click="closeUpdateSlideout()">
          <v-icon left>mdi-chevron-left</v-icon> Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!valid || slideouts.isLoading || !slideouts.hasChanges"
          @click="updateConfirmed()"
          :loading="slideouts.isLoading">
          <v-icon left>{{ selected.id ? 'mdi-check' : 'mdi-plus' }}</v-icon>
          {{ selected.id ? 'Save Changes' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </template>
  </slide-out>
</template>

<script>
  import formRules from '@Share/config/form-rules';
  import RolesSelector from '@Share/components/RolesSelector.vue';
  import { $handleError, $isEqual } from '@/utils';
  import moment from 'dayjs';
  import facilityService from '@Facility/service/facility.service';

  export default {
    name: 'edit-user',
    data() {
      return {
        valid: false,
        formRules,
        isFetching: false,
        selected: {},
        modal: false,
        selectedCemented: {},
        facilitiesList: [],
        facilitySearch: '',
        showSelectedOnly: false,
        slideouts: {
          tab: 0,
          active: false,
          isLoading: false,
          isClosing: false,
          hasChanges: false,
        },
      };
    },
    props: {
      nested: {
        type: Boolean,
        default: false,
      },
    },
    created() {
      this.fetchFacilities();
    },
    mounted() {
      this.$refs.updateForm.resetValidation();
    },
    computed: {
      createdDateAsText() {
        if (this.selected == null) return 'N/A';
        return moment(this.selected.createDate).format('YYYY/MM/DD, hh:mm A');
      },
      sideOutMinSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_MINSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_MINSIZE', val);
        },
      },
      sideOutCurrentSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_CURRENTSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_CURRENTSIZE', val);
        },
      },
      selectedFacilitiesCount() {
        const ids = this.selected.facilityIds || [];
        if (!ids.length || !this.facilitiesList.length) return 0;
        const validIds = new Set(this.facilitiesList.map((facility) => facility.id));
        return ids.filter((id) => validIds.has(id)).length;
      },
      visibleFacilities() {
        let list = this.facilitiesList;
        if (this.showSelectedOnly) {
          const ids = this.selected.facilityIds || [];
          list = list.filter((facility) => ids.includes(facility.id));
        }
        if (this.facilitySearch) {
          const query = this.facilitySearch.toLowerCase();
          list = list.filter((facility) => (facility.name || '').toLowerCase().includes(query));
        }
        return list;
      },
      allVisibleFacilitiesSelected() {
        const ids = this.selected.facilityIds || [];
        return (
          this.visibleFacilities.length > 0 &&
          this.visibleFacilities.every((facility) => ids.includes(facility.id))
        );
      },
      someVisibleFacilitiesSelected() {
        const ids = this.selected.facilityIds || [];
        return this.visibleFacilities.some((facility) => ids.includes(facility.id));
      },
      selectAllFacilitiesIcon() {
        if (this.allVisibleFacilitiesSelected) return 'mdi-close-box';
        if (this.someVisibleFacilitiesSelected) return 'mdi-minus-box';
        return 'mdi-checkbox-blank-outline';
      },
    },
    methods: {
      OnResize() {
        let userSlideout = this.$refs.userSlideout;
        this.sideOutCurrentSize = userSlideout.resizeValue;
      },
      onSlideoutClosing() {
        this.$emit('close');
        this.closeUpdateSlideout();
      },
      updateConfirmed() {
        this.slideouts.isLoading = true;
        let toSend = structuredClone(this.selected);
        this.$store
          .dispatch('USER/UPDATE', toSend)
          .then(() => {
            this.closeUpdateSlideout();
          })
          .finally(() => {
            this.slideouts.isLoading = false;
          });
      },
      closeUpdateSlideout() {
        this.selected = {};
        this.cementUser();
        this.slideouts.active = false;
        this.$refs.updateForm.resetValidation();
      },
      open(id) {
        if (id != null) {
          this.userId = id;
          this.fetchUser();
          setTimeout(() => {
            this.slideouts.tab = 0;
          }, 10);
        }
        this.slideouts.active = true;
      },
      fetchUser() {
        this.isFetching = true;
        this.$store
          .dispatch('USER/GET_BY_ID', this.userId)
          .then((resp) => {
            this.isFetching = false;
            this.slideouts.isLoading = false;
            this.selected = structuredClone(resp.data);
            this.cementUser();
          })
          .catch((err) => {
            this.isFetching = false;
            this.closeUpdateSlideout();
            $handleError(err);
          });
      },
      cementUser() {
        this.selectedCemented = structuredClone(this.selected);
        this.checkForChanges();
      },
      checkForChanges() {
        if (!this.slideouts.active) return;

        this.slideouts.hasChanges = !$isEqual(this.selected, this.selectedCemented);
      },
      toggleAllFacilities() {
        const current = this.selected.facilityIds || [];
        const visibleIds = this.visibleFacilities.map((facility) => facility.id);
        let next;
        if (this.allVisibleFacilitiesSelected) {
          next = current.filter((id) => !visibleIds.includes(id));
        } else {
          next = [...new Set([...current, ...visibleIds])];
        }
        this.$set(this.selected, 'facilityIds', next);
      },
      fetchFacilities() {
        facilityService.typeHead().then((resp) => {
          this.facilitiesList = resp.data;
        });
      },
    },
    components: { RolesSelector },
    watch: {
      selected: {
        handler() {
          this.checkForChanges();
        },
        deep: true,
      },
      nested: {
        handler() {
          this.nested ? (this.sideOutCurrentSize = '80vw') : (this.sideOutCurrentSize = '35vw');
          this.nested ? (this.sideOutMinSize = '80vw') : (this.sideOutMinSize = '35vw');
        },
        deep: true,
      },
    },
  };
</script>

<style scoped>
  .facilities-select-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  .theme--dark .facilities-select-header {
    background: #1e1e1e;
  }

  .facilities-select-header .v-list-item {
    min-height: 36px;
  }

  /* Compact the attached dropdown list */
  .facilities-select ::v-deep .v-menu__content .v-list {
    padding-top: 0;
    padding-bottom: 4px;
  }

  .facilities-select ::v-deep .v-menu__content .v-list-item {
    min-height: 32px;
    padding: 0 12px;
  }

  .facilities-select ::v-deep .v-menu__content .v-list-item__action {
    margin: 4px 12px 4px 0;
  }

  .facilities-select ::v-deep .v-menu__content .v-list-item__content {
    padding: 4px 0;
  }

  .facilities-select ::v-deep .v-menu__content .v-list-item__title {
    font-size: 0.8125rem;
  }
</style>
